/* The month at a glance — STORY-014.
 *
 * ── one read, not two ───────────────────────────────────────────────────────
 * A calendar composes Events and Tasks, and the rule about what belongs where
 * is a COMPOSITION rule this story settled: an undated Event and an unfinished
 * undated Task both appear, and a finished undated Task does not. Assembling
 * that from two list endpoints would put the rule in every client, and a rule
 * expressed in a UI is one that will differ between two of them.
 *
 * It also means one instant. A month whose Events came from one read and whose
 * Tasks came from another can show a Task on a day whose Event was already
 * deleted — small, but this screen is the one people trust to be complete.
 *
 * ── dates are calendar days, never instants ─────────────────────────────────
 * Every date here is `YYYY-MM-DD` and every one of them means the day at the
 * Venue — which is what the storage model already holds. Nothing converts an
 * instant into a day anywhere on this path, because that conversion is the bug
 * the story's timezone criterion describes.
 */
import { EVENT_STATUSES, TASK_PRIORITIES, TASK_STATUSES } from '@eventuley/domain';
import * as z from 'zod';
/** `YYYY-MM`. The unit the screen moves in. */
export const monthString = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'A month, as YYYY-MM.');
export const calendarEventSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Null for an Event nobody has committed to a day — an idea. */
    date: z.string().nullable(),
    status: z.enum(EVENT_STATUSES),
    venue: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export const calendarTaskSchema = z.object({
    id: z.string(),
    description: z.string(),
    dueOn: z.string().nullable(),
    status: z.enum(TASK_STATUSES),
    priority: z.enum(TASK_PRIORITIES),
});
export const calendarResponseSchema = z.object({
    month: z.string(),
    /** Every Event with a date inside the month. */
    events: z.array(calendarEventSchema),
    /** Every unfinished Task due inside the month. */
    tasks: z.array(calendarTaskSchema),
    /**
     * Events with no date, and unfinished Tasks with no due date.
     *
     * **Never silently omitted**, which is the whole reason this field exists.
     * An idea-stage Event would otherwise vanish from the only screen that shows
     * everything, and an undated Task wins the same argument — raised as an open
     * question 2026-08-13 and decided 2026-08-14, because one screen cannot
     * answer it both ways.
     *
     * A FINISHED undated Task is not here. It is not work that has been
     * forgotten, which is what this area is for.
     */
    undatedEvents: z.array(calendarEventSchema),
    undatedTasks: z.array(calendarTaskSchema),
});
//# sourceMappingURL=calendar.js.map